import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';

// How long the toast stays visible before auto-dismissing (ms).
const AUTO_DISMISS_MS = 6000;

/**
 * SaveErrorToast — surfaces a failed backend save outside the header button.
 *
 * Both the manual save and the 30s auto-save funnel through the same
 * `saveStatus` state machine (idle | saving | saved | error | session-expired),
 * so reacting to the transition into an error state covers both paths —
 * including the otherwise silent auto-save failure (#495 C-1 + H-1).
 *
 * Two distinct failures, two messages (#679):
 * - 'error': transient (network, conflict). The auto-save keeps trying, the
 *   toast says so and auto-dismisses.
 * - 'session-expired': the session cookie is gone, every retry would fail the
 *   same way. The auto-save has stopped, so the toast must not promise a retry
 *   and must not auto-dismiss — the user has to act (reload/log in), and the
 *   changes only live in this browser until they do.
 *
 * @param {Object} props
 * @param {'idle'|'saving'|'saved'|'error'|'session-expired'} props.saveStatus - current save state
 */
export default function SaveErrorToast({ saveStatus }) {
  const [dismissed, setDismissed] = useState(false);
  const [prevStatus, setPrevStatus] = useState(saveStatus);

  // Reset the dismissed flag on every status change (React's "adjust state
  // during render" pattern — re-arms the toast for each new error episode
  // without a mirroring effect).
  if (saveStatus !== prevStatus) {
    setPrevStatus(saveStatus);
    setDismissed(false);
  }

  const sessionExpired = saveStatus === 'session-expired';
  const visible = (saveStatus === 'error' || sessionExpired) && !dismissed;

  // Auto-dismiss timer. setState only fires from the (async) callback, so this
  // does not trigger a synchronous cascading render. The session-expired case
  // stays put until dismissed by hand — it needs a decision, not a glance.
  useEffect(() => {
    if (!visible || sessionExpired) return undefined;
    const timer = setTimeout(() => setDismissed(true), AUTO_DISMISS_MS);
    return () => clearTimeout(timer);
  }, [visible, sessionExpired]);

  if (!visible) return null;

  return (
    <div
      role="alert"
      className="fixed top-20 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded shadow-lg max-w-[90vw]"
    >
      <AlertTriangle size={16} className="flex-shrink-0" aria-hidden="true" />
      <span className="text-sm">
        {sessionExpired
          ? 'Deine Sitzung ist abgelaufen. Deine Änderungen liegen noch in diesem Browser — lade die Seite neu, um weiter zu speichern.'
          : 'Speichern fehlgeschlagen — wird erneut versucht.'}
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Meldung schließen"
        className="flex-shrink-0 ml-1 p-0.5 rounded hover:bg-red-700 transition-colors"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
